import React, { Suspense, lazy, useEffect, useState } from 'react';
import { HashRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';

// Providers (keep these eager - they're needed immediately)
import { I18nProvider } from '@/core/i18n/I18nProvider';
import { AuthProvider, useAuth } from '@/core/auth/AuthProvider';
import { SentryErrorBoundary } from '@/sentry';

// Native services (Apple App Store requirement)
import { initializeNativeServices } from '@/core/services/native';
import { HealthDisclaimer } from '@/shared/components/HealthDisclaimer';
import { Logger } from '@/core/utils';

// Semantic Layer - Búsqueda inteligente
import { semanticLayerService } from '@/core/semantic';

// Analytics - Google Analytics 4 + Firebase Analytics
import { initializeAnalytics, trackPageView, setAnalyticsUser, clearAnalyticsUser } from '@/core/services/analytics';
import { usePageTracking } from '@/core/services/analytics';

// Cookie Consent Banner (GDPR)
import { CookieConsentBanner } from '@/shared/components/CookieConsentBanner';

const logger = Logger.getLogger('App');

// Lazy load main app and auth screens
const AuthenticatedApp = lazy(() => import('@/app/navigation/AuthenticatedApp'));
const TherapistApp = lazy(() => import('@/apps/therapist/features/shared/TherapistApp'));
const SchoolApp = lazy(() => import('@/apps/school/features/components/SchoolApp'));
const WelcomeScreen = lazy(() => import('@/apps/user/features/auth/components/WelcomeScreen'));
const LoginScreen = lazy(() => import('@/apps/user/features/auth/components/LoginScreen'));
const SignupScreen = lazy(() => import('@/apps/user/features/auth/components/SignupScreen'));
const TermsOfService = lazy(() => import('@/apps/user/features/legal/components/TermsOfService'));
const DataProcessingAgreement = lazy(() => import('@/apps/user/features/legal/components/DataProcessingAgreement'));
const PrivacyPolicyPublic = lazy(() => import('@/apps/user/features/legal/components/PrivacyPolicy'));

// 🌐 PUBLIC PAGES - Directorio de terapeutas y Blog (accesible sin login)
const TherapistDirectoryPublic = lazy(() => import('@/apps/public/pages/TherapistDirectory'));
const TherapistProfilePublic = lazy(() => import('@/apps/public/pages/TherapistProfile'));
const AskExpert = lazy(() => import('@/apps/public/pages/AskExpert'));
const BlogFeed = lazy(() => import('@/apps/public/pages/BlogFeed'));
const BlogArticle = lazy(() => import('@/apps/public/pages/BlogArticle'));
// AssistantLanding eliminado - la funcionalidad está en /psicologos
// 🔒 Página de aceptación de consentimientos (GDPR) - Paciente acepta directamente
const AcceptConsent = lazy(() => import('@/apps/public/pages/AcceptConsent'));

// 📋 CLAIMABLE PROFILES - Directorio SEO con perfiles reclamables (Doctoralia, Colegios, etc.)
const ClaimableDirectoryPage = lazy(() => import('@/apps/public/pages/TherapistDirectoryPage'));
const ClaimProfilePage = lazy(() => import('@/apps/public/pages/ClaimProfilePage'));
const TherapistPublicProfilePage = lazy(() => import('@/apps/public/pages/TherapistPublicProfilePage'));

// 🌐 LANDINGS PARA TERAPEUTAS - Páginas de producto y precios
const TherapistLanding = lazy(() => import('@/shared/landings/TherapistLanding'));
const AcompanamientoLanding = lazy(() => import('@/shared/landings/modules/AcompanamientoLanding'));
const ContenidoLanding = lazy(() => import('@/shared/landings/modules/ContenidoLanding'));
const PracticasLanding = lazy(() => import('@/shared/landings/modules/PracticasLanding'));
const AgendaLanding = lazy(() => import('@/shared/landings/modules/AgendaLanding'));
const FinanzasLanding = lazy(() => import('@/shared/landings/modules/FinanzasLanding'));
const AsistenteLanding = lazy(() => import('@/shared/landings/modules/AsistenteLanding'));
const PreciosLanding = lazy(() => import('@/shared/landings/modules/PreciosLanding'));

// 🤝 PORTAL DE AFILIADOS - Login y acceso para comerciales
const AffiliateLogin = lazy(() =>
    import('@/apps/affiliate/AffiliateLogin').catch(err => {
        // Error: 'Error loading AffiliateLogin:', err);
        throw err;
    })
);
const AffiliatePortal = lazy(() =>
    import('@/apps/affiliate/AffiliatePortal').catch(err => {
        // Error: 'Error loading AffiliatePortal:', err);
        throw err;
    })
);

const LoadingSpinner: React.FC = () => (
    <div className="flex items-center justify-center h-screen bg-stone-50">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-amber-600"></div>
    </div>
);


const AppRoutes: React.FC = () => {
    const { currentUser, loading, currentProfile } = useAuth();
    const location = useLocation();

    // 📊 Track page views automatically on route changes
    usePageTracking();

    if (loading) {
        return <LoadingSpinner />;
    }

    // 🌐 Rutas públicas del directorio de terapeutas y blog - SIEMPRE accesibles
    if (location.pathname.startsWith('/terapeutas') || location.pathname.startsWith('/preguntas') || location.pathname.startsWith('/blog') || location.pathname.startsWith('/aceptar-consentimiento')) {
        return (
            <Suspense fallback={<LoadingSpinner />}>
                <Routes>
                    {/* Landing antigua eliminada - redirigir a la nueva */}
                    <Route path="/terapeutas" element={<Navigate to="/psicologos" replace />} />
                    {/* Perfiles de terapeutas registrados - MANTENER */}
                    <Route path="/terapeutas/:slugOrId" element={<TherapistProfilePublic />} />
                    <Route path="/preguntas" element={<AskExpert />} />
                    <Route path="/preguntas/:slug" element={<AskExpert />} />
                    <Route path="/blog" element={<BlogFeed />} />
                    <Route path="/blog/:slug" element={<BlogArticle />} />
                    {/* 🔒 Aceptación de consentimientos GDPR - El paciente acepta directamente */}
                    <Route path="/aceptar-consentimiento/:token" element={<AcceptConsent />} />
                </Routes>
            </Suspense>
        );
    }

    // 📋 DIRECTORIO SEO - Perfiles reclamables (Doctoralia, Colegios, etc.)
    if (location.pathname.startsWith('/psicologos') || location.pathname.startsWith('/psicologo/') || location.pathname.startsWith('/reclamar-perfil')) {
        return (
            <Suspense fallback={<LoadingSpinner />}>
                <Routes>
                    {/* Directorio por ciudad y especialidad */}
                    <Route path="/psicologos" element={<ClaimableDirectoryPage />} />
                    <Route path="/psicologos/:city" element={<ClaimableDirectoryPage />} />
                    <Route path="/psicologos/:city/:specialty" element={<ClaimableDirectoryPage />} />
                    {/* Perfil individual enriquecido */}
                    <Route path="/psicologo/:profileId" element={<TherapistPublicProfilePage />} />
                    {/* Reclamar perfil */}
                    <Route path="/reclamar-perfil" element={<ClaimProfilePage />} />
                    <Route path="/reclamar-perfil/:profileId" element={<ClaimProfilePage />} />
                </Routes>
            </Suspense>
        );
    }

    // 🌐 LANDINGS PARA TERAPEUTAS - Páginas de producto y precios
    if (location.pathname.startsWith('/para-terapeutas') || location.pathname === '/precios') {
        return (
            <Suspense fallback={<LoadingSpinner />}>
                <Routes>
                    {/* Hub principal de terapeutas */}
                    <Route path="/para-terapeutas" element={<TherapistLanding />} />
                    {/* Landings de módulos específicos */}
                    <Route path="/para-terapeutas/acompanamiento" element={<AcompanamientoLanding />} />
                    <Route path="/para-terapeutas/contenido" element={<ContenidoLanding />} />
                    <Route path="/para-terapeutas/practicas" element={<PracticasLanding />} />
                    <Route path="/para-terapeutas/agenda" element={<AgendaLanding />} />
                    <Route path="/para-terapeutas/finanzas" element={<FinanzasLanding />} />
                    <Route path="/para-terapeutas/asistente" element={<AsistenteLanding />} />
                    {/* Precios */}
                    <Route path="/precios" element={<PreciosLanding />} />
                </Routes>
            </Suspense>
        );
    }

    // 🤝 Portal de afiliados - Login para comerciales
    if (location.pathname === '/affiliate-login' || location.pathname === '/login-afiliado') {
        return (
            <Suspense fallback={<LoadingSpinner />}>
                <AffiliateLogin />
            </Suspense>
        );
    }

    // 🤝 Portal de afiliados - Acceso directo (renderiza directamente el portal, que maneja la autenticación internamente)
    // IMPORTANTE: Esta condición debe estar ANTES de las rutas públicas para que funcione
    if (location.pathname === '/affiliate' || location.pathname === '/portal-afiliado') {
        logger.info('Rendering AffiliatePortal', { pathname: location.pathname });
        return (
            <Suspense fallback={<LoadingSpinner />}>
                <AffiliatePortal />
            </Suspense>
        );
    }

    // ✅ Rutas públicas siempre accesibles (evita "autologin" que se come el /signup)
    // Especialmente importante para invitaciones del terapeuta vía link.
    // Las páginas legales también son públicas — RGPD obliga a que el usuario
    // pueda leerlas ANTES de aceptar.
    if (
      location.pathname === '/signup' ||
      location.pathname === '/login' ||
      location.pathname === '/welcome' ||
      location.pathname === '/terms' ||
      location.pathname === '/privacy' ||
      location.pathname === '/dpa'
    ) {
        return (
            <Suspense fallback={<LoadingSpinner />}>
                <UnauthenticatedApp />
            </Suspense>
        );
    }

    return (
        <Suspense fallback={<LoadingSpinner />}>
            {currentUser ? <AuthenticatedRoutes currentProfile={currentProfile} /> : <UnauthenticatedApp />}
        </Suspense>
    );
};

const AuthenticatedRoutes: React.FC<{ currentProfile: any }> = ({ currentProfile }) => {
    const { currentUser } = useAuth();
    const location = useLocation();

    // 🔐 RUTA ESPECIAL: Panel de Admin siempre usa AuthenticatedApp (independiente del perfil activo)
    if (location.pathname.startsWith('/admin')) {
        return <AuthenticatedApp />;
    }

    // 🤝 RUTA ESPECIAL: Portal de Afiliados siempre usa AuthenticatedApp (independiente del perfil activo)
    if (location.pathname === '/affiliate' || location.pathname === '/portal-afiliado') {
        return <AuthenticatedApp />;
    }

    // NUEVO: Routing basado en perfil activo (multi-rol)
    if (currentProfile) {
        switch (currentProfile.type) {
            case 'therapist':
                return <TherapistApp />;
            case 'school':
            case 'school_admin':
            case 'school_editor':
                return <SchoolApp />;
        }
    }

    // FALLBACK: Si no hay perfil pero tiene rol antiguo, usar ese
    if (currentUser && currentUser.role === 'therapist') {
        return <TherapistApp />;
    }

    // Fallback: App de usuario por defecto
    return <AuthenticatedApp />;
};

const App: React.FC = () => {
    // App ahora solo contiene el Router y el AppRoutes
    return (
        <Router>
            <AppRoutes />
        </Router>
    );
};

const UnauthenticatedApp: React.FC = () => {
    return (
        <Routes>
            <Route path="/" element={<WelcomeScreen />} />
            <Route path="/welcome" element={<WelcomeScreen />} />
            <Route path="/login" element={<LoginScreen />} />
            <Route path="/signup" element={<SignupScreen />} />
            {/* Therapist onboarding should also be available when unauthenticated */}
            <Route path="/therapist/onboarding" element={<SignupScreen />} />
            {/* Legal pages — accessible from signup before login (required by GDPR) */}
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/privacy" element={<PrivacyPolicyPublic />} />
            <Route path="/dpa" element={<DataProcessingAgreement />} />
            <Route path="*" element={<Navigate to="/welcome" />} />
        </Routes>
    );
};

// This is the new component that will be rendered by index.tsx
const AppWrapper: React.FC = () => {
    const [nativeServicesReady, setNativeServicesReady] = useState(false);

    // Initialize native services on app start (Apple App Store requirement)
    useEffect(() => {
        const initServices = async () => {
            try {
                // 1. Native services (Capacitor)
                await initializeNativeServices();
                logger.info('✅ Native services initialized');

                // 2. Semantic Layer (búsqueda inteligente)
                await semanticLayerService.initialize();
                logger.info('✅ Semantic Layer initialized');

                // 3. Analytics (Google Analytics 4 + Firebase Analytics)
                await initializeAnalytics();
                logger.info('✅ Analytics initialized');
            } catch (error) {
                logger.error('Error initializing services', error as Error);
            } finally {
                setNativeServicesReady(true);
            }
        };
        initServices();
    }, []);

    return (
        <SentryErrorBoundary
            fallback={({ error, resetError }) => (
                <div className="flex items-center justify-center min-h-screen bg-slate-50 p-4">
                    <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-6">
                        <h1 className="text-2xl font-bold text-red-600 mb-4">
                            ⚠️ Algo salió mal
                        </h1>
                        <p className="text-slate-600 mb-4">
                            Lo sentimos, ocurrió un error inesperado. Nuestro equipo ha sido notificado.
                        </p>
                        <details className="mb-4">
                            <summary className="cursor-pointer text-sm text-slate-500 hover:text-slate-700">
                                Ver detalles técnicos
                            </summary>
                            <pre className="mt-2 text-xs bg-slate-100 p-3 rounded overflow-auto max-h-40">
                                {error?.toString()}
                            </pre>
                        </details>
                        <button
                            onClick={resetError}
                            className="w-full bg-amber-600 text-white py-2 px-4 rounded-lg hover:bg-amber-700 transition-colors"
                        >
                            Intentar nuevamente
                        </button>
                    </div>
                </div>
            )}
            showDialog={import.meta.env.MODE === 'development'}
        >
            <I18nProvider>
                <AuthProvider>
                        {/* Health Disclaimer deshabilitado - demasiado invasivo */}
                        {/* <HealthDisclaimer variant="modal" showOnce={true} /> */}
                        <App />
                        {/* Cookie Consent Banner - GDPR compliance */}
                        <CookieConsentBanner />
                </AuthProvider>
            </I18nProvider>
        </SentryErrorBoundary>
    );
};

export default AppWrapper;